import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useXFlowPartnerContext } from "../../context/XFlowPartners";
import { xflow_reconcile_receivables } from "../../constants/apis";

import { useAppContext } from "../../context/AppContext";
import { XflowReceivableRes } from "../../types/XFlow";
import { toast } from "react-toastify";
import { useMemo, useRef, useState } from "react";
import { SpinnerIcon } from "../../components/Icons";
import { TableRowSkeleton } from "../../components/Dashboard/Quotes";
import { PartnerSkeleton } from "../../components/XFlowPayment/PartnersList";
import Modal from "../../components/Modal";
// import CreateReceivableModalForm from "../../components/XFlowPayment/CreateReceivableForm";
import CreateReceivableForm from "../../components/XFlowPayment/FormSteps/CreateReceivable";

import ReconcileModalBtn from "../../components/XFlowPayment/ReconcileModalBtn";
import { useXFlowReceivablesContext } from "../../context/XFlowReceivables";

// total receivable sum
function sumReceivableAmounts(data: XflowReceivableRes[]) {
  let totalMaximumReconcilable = 0.0;
  let totalReconcilable = 0.0;
  let totalReconciled = 0.0;
  let totalReconciledNotSettled = 0.0;

  for (const item of data) {
    if (item.status === "draft") continue;

    totalMaximumReconcilable += parseFloat(
      item.amount_maximum_reconcilable || "0"
    );
    totalReconcilable += parseFloat(item.amount_reconcilable || "0");
    totalReconciled += parseFloat(item.amount_reconciled || "0");
    totalReconciledNotSettled += parseFloat(
      item.amount_reconciled_not_settled || "0"
    );
  }

  return {
    totalMaximumReconcilable,
    totalReconcilable,
    totalReconciled,
    totalReconciledNotSettled,
  };
}

export default function XFlowReceivables() {
  // const { id: partner_id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const { data: allPartners, loading } = useXFlowPartnerContext();

  const navigate = useNavigate();
  const partner_id = searchParams.get("partner_id");

  const partner = allPartners?.find((partner) => partner.id === partner_id);

  const {
    data: receivablesData,
    loading: receivablesLoading,
    refetch: refetchReceivables,
  } = useXFlowReceivablesContext();

  // console.log(receivablesData);

  const handleBack = () => {
    navigate("/simple-payment/receivable");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "activated":
        return "text-blue-400 bg-blue-50 border border-blue-400 rounded-xl";
      case "completed":
        return "text-green-400 bg-green-50 border border-green-400 rounded-xl";
      case "draft":
        return "text-orange-400 bg-orange-50 border border-orange-400 rounded-xl";
      case "cancelled":
        return "text-red-400 bg-red-50 border border-red-400 rounded-xl";

      default:
        return "";
    }
  };

  return (
    <section>
      <div>
        <button className="text-sm underline" onClick={handleBack}>
          &larr; Go back
        </button>
      </div>

      <div className="p-4 grid grid-cols-2 gap-1 border-b">
        {loading && <PartnerSkeleton />}
        {partner && (
          <div key={partner.id}>
            <div className="space-y-2 py-1 pb-4">
              <h3 className="flex items-center gap-1">
                {partner.nickname}{" "}
                <span
                  className={`text-[10px] ${
                    partner.status === "verifying" && "text-orange-400"
                  } ${partner.status === "activated" && "text-green-400"}`}
                >
                  {partner.status}
                </span>
              </h3>
              <div className="text-sm">
                <p>{partner.business_details.legal_name}</p>
                <p>{partner.business_details.email}</p>
              </div>
              <div className="text-[10px] text-[#344054]">
                <p>{partner.business_details.physical_address.line1}</p>
                <p>{partner.business_details.physical_address.postal_code}</p>
                <p>{`${partner.business_details.physical_address.city}, ${partner.business_details.physical_address.state}, ${partner.business_details.physical_address.country}`}</p>
              </div>
              {/* <div>
              {partner.xflow_response.http_status_code === 400 &&
                partner.xflow_response.errors.map((err) => (
                  <p key={err.code} className="text-[10px] text-orange-400">
                    {err.message}
                  </p>
                ))}
            </div> */}

              {/* <div className="space-x-2">
              <button
                type="button"
                className="bg-custom-blue-medium text-white px-2 py-[2px] rounded-md"
              >
                Create Receivable
              </button>
              <button
                type="button"
                className="text-custom-blue-medium border border-custom-blue-medium px-2 py-[2px] rounded-md"
              >
                Reconcile Receivable
              </button>
            </div> */}
            </div>

            {partner.status === "activated" && (
              <div className="flex items-center gap-3">
                <CreateReceivableBtn />
                {receivablesData?.some((el) => el.status === "activated") && (
                  <ReconcileModalBtn
                    receivablesData={receivablesData}
                    refetchReceivables={refetchReceivables}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {receivablesLoading && <PartnerSkeleton />}

        {receivablesData && receivablesData.length > 0 && (
          <ReceivableAmmountSumCard receivablesData={receivablesData} />
        )}
      </div>
      <div className="p-4">
        <h3 className="font-medium text-gray-700 my-4">Receivables</h3>

        <table className="min-w-full border border-[#EAECF0] text-left rounded-xl border-separate">
          <thead>
            <tr>
              <th className="font-medium text-sm text-[#475467] px-4 py-3">
                Created at
              </th>
              <th className="font-medium text-sm text-[#475467]">
                Invoice number
              </th>
              <th className="font-medium text-sm text-[#475467]">
                Receivable Amount
              </th>
              <th className="font-medium text-sm text-[#475467]">
                Amount pending
              </th>
              <th className="font-medium text-sm text-[#475467]">Status</th>
              {/* <th className="font-medium text-sm text-[#475467]">Action</th> */}
            </tr>
          </thead>

          <tbody className="text-gray-500">
            {receivablesLoading ? (
              [1, 2, 3].map((el) => <TableRowSkeleton key={el} />)
            ) : receivablesData && receivablesData.length > 0 ? (
              receivablesData.map((el) => (
                <tr key={el.id}>
                  <td className="p-4 border-t text-sm border-[#EAECF0]">
                    {new Date(el.created * 1000).toDateString()}
                  </td>
                  <td className="border-t text-sm border-[#EAECF0]">
                    {el.invoice.reference_number}
                  </td>
                  <td className="border-t text-sm border-[#EAECF0]">
                    {`${el.currency} ${el.amount_maximum_reconcilable}`}
                  </td>
                  <td className="border-t text-sm border-[#EAECF0]">
                    {el.amount_reconcilable
                      ? `${el.currency} ${el.amount_reconcilable}`
                      : "nil"}
                  </td>

                  <td className={`border-t text-sm border-[#EAECF0]`}>
                    <p
                      className={`inline-flex justify-center px-2 ${getStatusColor(
                        el.status
                      )}`}
                    >
                      {el.status}
                    </p>
                  </td>
                  {/* <td className="border-t text-sm border-[#EAECF0]">
                    <ReconcileBtn
                      el={el}
                      refetchReceivables={refetchReceivables}
                    />
                  </td> */}
                </tr>
              ))
            ) : (
              <tr className="p-4 text-center text-sm">
                <td>No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReconcileBtn({
  el,
  refetchReceivables,
}: {
  el: XflowReceivableRes;
  refetchReceivables: () => void;
}) {
  const [reconcileBtnLoading, setReconcileBtnLoading] = useState(false);
  const { jwtToken } = useAppContext();

  const reconcileReceivables = async (
    receivablesId: number | string,
    amount: string
  ) => {
    setReconcileBtnLoading(true);

    try {
      const res = await fetch(xflow_reconcile_receivables, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify({ receivables_id: receivablesId, amount }),
      });
      const data = await res.json();

      if (data.status === "success") {
        toast.success(
          data?.message || data?.msg || "Amount reconcile is complete"
        );
        refetchReceivables();
      } else {
        toast.error("something went wrong!");
      }
    } catch (error) {
      console.log(error);
      toast.error("something went wrong!");
    } finally {
      setReconcileBtnLoading(false);
    }
  };

  return el.status === "activated" ? (
    <button
      type="button"
      className="text-custom-blue-medium border border-custom-blue-medium w-[86px] px-2 py-[2px] flex items-center justify-center rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
      disabled={reconcileBtnLoading}
      onClick={() => {
        reconcileReceivables(el.id, el.amount_maximum_reconcilable);
      }}
    >
      {reconcileBtnLoading ? (
        <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-custom-blue-medium" />
      ) : (
        "Reconcile"
      )}
    </button>
  ) : el.status === "draft" ? (
    <p>processing</p>
  ) : (
    el.status === "completed" && <p>Done</p>
  );
}

function ReceivableAmmountSumCard({
  receivablesData,
}: {
  receivablesData: XflowReceivableRes[];
}) {
  const {
    totalMaximumReconcilable,
    totalReconcilable,
    totalReconciled,
    totalReconciledNotSettled,
  } = useMemo(
    () => sumReceivableAmounts(receivablesData || []),
    [receivablesData]
  );

  return (
    <div className="text-[#242424] space-y-2">
      <h3 className="text-sm">USD receivable value</h3>
      <p className="text-2xl font-medium">USD {totalMaximumReconcilable}</p>
      <div className="h-2 bg-custom-semi-light-blue/50 rounded-lg">
        <div
          className="h-2 bg-custom-dark-blue rounded-lg"
          style={{
            width: (totalReconciled / totalMaximumReconcilable) * 100 + "%",
          }}
        ></div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div>
          <div className="flex items-center gap-1">
            <div className="size-2 bg-custom-dark-blue rounded-full" />
            <p>Reconciled</p>
          </div>
          <p>USD {totalReconciled}</p>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <div className="size-2 bg-orange-300 rounded-full" />
            <p>Processing</p>
          </div>
          <p>USD {totalReconciledNotSettled}</p>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <div className="size-2 bg-custom-semi-light-blue/50 rounded-full" />

            <p>Pending</p>
          </div>
          <p>USD {totalReconcilable}</p>
        </div>
      </div>
    </div>
  );
}

function CreateReceivableBtn() {
  const receivableModalRef = useRef<HTMLDialogElement>(null);
  // const { id: partner_id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();

  const partner_id = searchParams.get("partner_id");

  const toggleReceivableModal = () => {
    if (receivableModalRef.current?.open) {
      receivableModalRef.current?.close();
    } else {
      receivableModalRef.current?.showModal();
    }
  };

  return (
    <div>
      <button
        type="button"
        className="text-white border bg-custom-blue-medium px-2 py-[2px] rounded-md"
        onClick={toggleReceivableModal}
      >
        Create Receivable
      </button>

      <Modal
        ref={receivableModalRef}
        toggleDialog={toggleReceivableModal}
        className="w-[650px] p-5 rounded-md backdrop:bg-black/50"
      >
        <h2 className="font-medium text-xl mb-4">Create receivable</h2>

        <CreateReceivableForm
          toggleReceivableModal={toggleReceivableModal}
          partnerId={partner_id!}
        />
      </Modal>
    </div>
  );
}
